import React, { createContext, useContext, useReducer, useEffect } from 'react';
import apiService, { VIDEO_CATEGORIES } from '../services/api';

// Initial state for the video store
const initialState = {
  videos: {
    [VIDEO_CATEGORIES.TRENDING_NOW]: [],
    [VIDEO_CATEGORIES.POPULAR_MOVIES]: [],
    [VIDEO_CATEGORIES.NEW_RELEASES]: [],
    [VIDEO_CATEGORIES.MY_LIST]: [],
    [VIDEO_CATEGORIES.CONTINUE_WATCHING]: [],
    [VIDEO_CATEGORIES.FEATURED]: null
  },
  loading: {
    [VIDEO_CATEGORIES.TRENDING_NOW]: false,
    [VIDEO_CATEGORIES.POPULAR_MOVIES]: false,
    [VIDEO_CATEGORIES.NEW_RELEASES]: false,
    [VIDEO_CATEGORIES.MY_LIST]: false,
    [VIDEO_CATEGORIES.CONTINUE_WATCHING]: false,
    [VIDEO_CATEGORIES.FEATURED]: false
  },
  errors: {
    [VIDEO_CATEGORIES.TRENDING_NOW]: null,
    [VIDEO_CATEGORIES.POPULAR_MOVIES]: null,
    [VIDEO_CATEGORIES.NEW_RELEASES]: null,
    [VIDEO_CATEGORIES.MY_LIST]: null,
    [VIDEO_CATEGORIES.CONTINUE_WATCHING]: null,
    [VIDEO_CATEGORIES.FEATURED]: null
  },
  currentVideo: null,
  pagination: {
    [VIDEO_CATEGORIES.TRENDING_NOW]: { offset: 0, limit: 8, hasMore: true },
    [VIDEO_CATEGORIES.POPULAR_MOVIES]: { offset: 0, limit: 8, hasMore: true },
    [VIDEO_CATEGORIES.NEW_RELEASES]: { offset: 0, limit: 8, hasMore: true },
    [VIDEO_CATEGORIES.MY_LIST]: { offset: 0, limit: 8, hasMore: true },
    [VIDEO_CATEGORIES.CONTINUE_WATCHING]: { offset: 0, limit: 8, hasMore: true }
  }
};

// Action types
export const VIDEO_ACTIONS = {
  // Loading actions
  SET_LOADING: 'SET_LOADING',
  
  // Video list actions
  SET_VIDEOS: 'SET_VIDEOS',
  APPEND_VIDEOS: 'APPEND_VIDEOS',
  
  // Featured video actions
  SET_FEATURED_VIDEO: 'SET_FEATURED_VIDEO',
  
  // Current video actions
  SET_CURRENT_VIDEO: 'SET_CURRENT_VIDEO',
  
  // Error actions
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Pagination actions
  UPDATE_PAGINATION: 'UPDATE_PAGINATION',
  
  // Reset actions
  RESET_CATEGORY: 'RESET_CATEGORY',
  RESET_ALL: 'RESET_ALL'
};

// Reducer function
const videoReducer = (state, action) => {
  switch (action.type) {
    case VIDEO_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.category]: action.loading
        }
      };

    case VIDEO_ACTIONS.SET_VIDEOS:
      return {
        ...state,
        videos: {
          ...state.videos,
          [action.category]: action.videos
        },
        loading: {
          ...state.loading,
          [action.category]: false
        },
        errors: {
          ...state.errors,
          [action.category]: null
        },
        // Initialiser la pagination pour les nouvelles catégories
        pagination: {
          ...state.pagination,
          [action.category]: state.pagination[action.category] || { offset: 0, limit: 8, hasMore: true }
        }
      };

    case VIDEO_ACTIONS.APPEND_VIDEOS:
      return {
        ...state,
        videos: {
          ...state.videos,
          [action.category]: [...(state.videos[action.category] || []), ...action.videos]
        },
        loading: {
          ...state.loading,
          [action.category]: false
        }
      };

    case VIDEO_ACTIONS.SET_FEATURED_VIDEO:
      return {
        ...state,
        videos: {
          ...state.videos,
          [VIDEO_CATEGORIES.FEATURED]: action.video
        },
        loading: {
          ...state.loading,
          [VIDEO_CATEGORIES.FEATURED]: false
        },
        errors: {
          ...state.errors,
          [VIDEO_CATEGORIES.FEATURED]: null
        }
      };

    case VIDEO_ACTIONS.SET_CURRENT_VIDEO:
      return {
        ...state,
        currentVideo: action.video
      };

    case VIDEO_ACTIONS.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.category]: action.error
        },
        loading: {
          ...state.loading,
          [action.category]: false
        }
      };

    case VIDEO_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.category]: null
        }
      };

    case VIDEO_ACTIONS.UPDATE_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          [action.category]: {
            ...state.pagination[action.category],
            ...action.pagination
          }
        }
      };

    case VIDEO_ACTIONS.RESET_CATEGORY:
      return {
        ...state,
        videos: {
          ...state.videos,
          [action.category]: action.category === VIDEO_CATEGORIES.FEATURED ? null : []
        },
        loading: {
          ...state.loading,
          [action.category]: false
        },
        errors: {
          ...state.errors,
          [action.category]: null
        },
        pagination: {
          ...state.pagination,
          [action.category]: { offset: 0, limit: 8, hasMore: true }
        }
      };

    case VIDEO_ACTIONS.RESET_ALL:
      return initialState;

    default:
      return state;
  }
};

// Create context
const VideoContext = createContext();

// Context provider component
export const VideoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(videoReducer, initialState);

  // Action creators avec mémorisation
  const actions = React.useMemo(() => ({
    // Set loading state
    setLoading: (category, loading) => {
      dispatch({ type: VIDEO_ACTIONS.SET_LOADING, category, loading });
    },

    // Fetch videos by category
    fetchVideosByCategory: async (category, options = {}) => {
      try {
        // Éviter les requêtes en double
        if (state.loading[category]) {
          console.log(`⚠️ Requête déjà en cours pour ${category}, ignorée`);
          return;
        }

        dispatch({ type: VIDEO_ACTIONS.SET_LOADING, category, loading: true });
        
        const { append = false, ...apiOptions } = options;
        
        // Assurez-vous que la pagination existe pour cette catégorie
        const currentPagination = state.pagination[category] || { offset: 0, limit: 8, hasMore: true };
        
        const result = await apiService.getVideosByCategory(category, {
          ...apiOptions,
          offset: append ? (currentPagination?.offset || 0) : 0
        });

        // Handle both old array format and new object format
        const videos = Array.isArray(result) ? result : result.videos || [];
        const pagination = result.pagination || {};

        if (append) {
          dispatch({ type: VIDEO_ACTIONS.APPEND_VIDEOS, category, videos });
        } else {
          dispatch({ type: VIDEO_ACTIONS.SET_VIDEOS, category, videos });
        }

        // Update pagination with API response data
        const newPagination = {
          offset: pagination.offset || (append ? (currentPagination?.offset || 0) + videos.length : videos.length),
          limit: pagination.limit || apiOptions.limit || 8,
          hasMore: pagination.hasNextPage !== undefined ? pagination.hasNextPage : videos.length === (apiOptions.limit || 8),
          totalDocs: pagination.totalDocs || 0,
          totalPages: pagination.totalPages || 1,
          page: pagination.page || 1
        };
        
        dispatch({
          type: VIDEO_ACTIONS.UPDATE_PAGINATION,
          category,
          pagination: newPagination
        });

        return { videos, pagination: newPagination, meta: result.meta };
      } catch (error) {
        dispatch({ type: VIDEO_ACTIONS.SET_ERROR, category, error: error.message });
        throw error;
      }
    },

    // Fetch featured video
    fetchFeaturedVideo: async (pathseo) => {
      try {
        dispatch({ type: VIDEO_ACTIONS.SET_LOADING, category: VIDEO_CATEGORIES.FEATURED, loading: true });
        
        const video = await apiService.getVideoByPathseo(pathseo);
        dispatch({ type: VIDEO_ACTIONS.SET_FEATURED_VIDEO, video });
        
        return video;
      } catch (error) {
        dispatch({ type: VIDEO_ACTIONS.SET_ERROR, category: VIDEO_CATEGORIES.FEATURED, error: error.message });
        throw error;
      }
    },

    // Set current video (for modals, etc.)
    setCurrentVideo: (video) => {
      dispatch({ type: VIDEO_ACTIONS.SET_CURRENT_VIDEO, video });
    },

    // Load more videos (pagination)
    loadMoreVideos: async (category, options = {}) => {
      return actions.fetchVideosByCategory(category, { ...options, append: true });
    },

    // Clear error for a category
    clearError: (category) => {
      dispatch({ type: VIDEO_ACTIONS.CLEAR_ERROR, category });
    },

    // Reset a specific category
    resetCategory: (category) => {
      dispatch({ type: VIDEO_ACTIONS.RESET_CATEGORY, category });
    },

    // Reset all data
    resetAll: () => {
      dispatch({ type: VIDEO_ACTIONS.RESET_ALL });
    }
  }), [state]);

  // Selectors avec mémorisation pour éviter les re-renders
  const selectors = React.useMemo(() => ({
    // Get videos by category
    getVideosByCategory: (category) => state.videos[category] || [],
    
    // Get featured video
    getFeaturedVideo: () => state.videos[VIDEO_CATEGORIES.FEATURED],
    
    // Get current video
    getCurrentVideo: () => state.currentVideo,
    
    // Get loading state
    isLoading: (category) => state.loading[category] || false,
    
    // Get error
    getError: (category) => state.errors[category],
    
    // Get pagination info
    getPagination: (category) => state.pagination[category] || { offset: 0, limit: 8, hasMore: true },
    
    // Check if category has more data to load
    hasMore: (category) => state.pagination[category]?.hasMore || false,
    
    // Get all categories loading state
    isAnyLoading: () => Object.values(state.loading).some(loading => loading),
    
    // Get videos count by category
    getVideosCount: (category) => (state.videos[category] || []).length
  }), [state]);

  const value = {
    ...state,
    actions,
    selectors
  };

  return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>;
};

// Custom hook to use the video context
export const useVideoStore = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideoStore must be used within a VideoProvider');
  }
  return context;
};

// Hook for specific category
export const useVideoCategory = (category) => {
  const { selectors, actions } = useVideoStore();
  
  return {
    videos: selectors.getVideosByCategory(category),
    loading: selectors.isLoading(category),
    error: selectors.getError(category),
    hasMore: selectors.hasMore(category),
    pagination: selectors.getPagination(category),
    fetchVideos: (options) => actions.fetchVideosByCategory(category, options),
    loadMore: (options) => actions.loadMoreVideos(category, options),
    clearError: () => actions.clearError(category),
    reset: () => actions.resetCategory(category)
  };
};

// Export everything
export { VIDEO_CATEGORIES };
export default VideoContext;
